import { motion } from 'framer-motion';
import { useState } from 'react';
import { Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Note: Replace 'YOUR_FORMSPREE_ID' with actual Formspree ID
    try {
      const response = await fetch('https://formspree.io/f/meerbgdl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Message sent! We\'ll get back to you soon.');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div data-testid="contact-page">
      {/* Page Header */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl sm:text-6xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-6"
          >
            Get in Touch
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-[#5C5855] leading-relaxed"
          >
            Questions about our handcrafted crocheted hats, custom orders, or shipping? Christian Clark typically responds within 1-2 business days.
          </motion.p>
        </div>
      </section>
      
      {/* Contact Content */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-6">
                Let's Connect
              </h2>
              <p className="text-[#5C5855] leading-relaxed mb-8">
                Whether you have questions about our products, want to discuss a custom order, or just want to say hello, we're here for you. Christian Clark typically responds within 1-2 business days.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#A85A43]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#A85A43]" />
                  </div>
                  <div>
                    <h3 className="font-[Manrope] font-semibold text-[#2A2626] mb-1">Email Us</h3>
                    <a
                      href="mailto:info@protectmycrown.com"
                      className="text-[#A85A43] hover:text-[#8C4935] transition-colors"
                      data-testid="contact-email-link"
                    >
                      info@protectmycrown.com
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-[#F9F6F0] rounded-lg border border-[#EADCCC]">
                <h3 className="text-lg font-[Playfair_Display] text-[#2A2626] mb-3">
                  Quick Answers
                </h3>
                <ul className="space-y-2 text-[#5C5855] text-sm">
                  <li><strong>Shipping:</strong> USPS from Sacramento, CA. In-stock items ship in 3-5 business days.</li>
                  <li><strong>Custom orders:</strong> 5-10 business days. Start at $30. Use our <a href="/products" className="text-[#A85A43] hover:text-[#8C4935] font-semibold">Products page</a> custom order form.</li>
                  <li><strong>Materials:</strong> Soft acrylic and cotton-blend yarns. Machine washable on gentle cycle.</li>
                  <li><strong>Returns:</strong> Contact us within 7 days of delivery for any issues.</li>
                </ul>
              </div>

              <div className="mt-6 p-6 bg-[#A85A43]/5 rounded-lg border border-[#EADCCC]">
                <h3 className="text-lg font-[Playfair_Display] text-[#2A2626] mb-3">
                  Custom Orders Welcome
                </h3>
                <p className="text-[#5C5855] text-sm leading-relaxed">
                  Looking for a specific hat design, color combination, or size? We create custom crocheted hats, scarves, and shawls. Visit our <a href="/products" className="text-[#A85A43] hover:text-[#8C4935] font-semibold">Products page</a> to submit a custom order request, or describe what you need in the message form here.
                </p>
              </div>
            </motion.div>
            
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white border border-[#EADCCC] rounded-lg p-8"
            >
              <h2 className="text-2xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-6">
                Send Us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-[#2A2626] font-[Manrope] font-semibold mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-[#EADCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#A85A43]"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-[#2A2626] font-[Manrope] font-semibold mb-2">
                    Your Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-[#EADCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#A85A43]"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-[#2A2626] font-[Manrope] font-semibold mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-[#EADCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#A85A43]"
                    placeholder="What's this about?"
                    data-testid="contact-subject-input"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-[#2A2626] font-[Manrope] font-semibold mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-[#EADCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#A85A43] resize-none"
                    placeholder="Tell us what's on your mind..."
                    data-testid="contact-message-textarea"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#A85A43] hover:bg-[#8C4935] disabled:bg-[#A85A43]/50 text-white font-[Manrope] py-4 px-8 rounded-full transition-colors flex items-center justify-center gap-2"
                  data-testid="contact-submit-button"
                >
                  <Send className="w-5 h-5" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
